/* eslint-disable camelcase */
// @mui
import { useMemo, useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Container, Divider, Grid, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { getYear } from 'date-fns';
import { useNavigate } from 'react-router-dom';

import Iconify from '../../components/Iconify';
import Image from '../../components/Image';
import { useSelector } from '../../redux/store';
import useLocales from '../../hooks/useLocales';
import palette from '../../theme/palette';
import conObj from '../../utils/connection-assistant';

const StyledTypography = styled(Typography)(({ theme }) => ({
  '&': {
    color: 'white',
    fontWeight: 600,
    textTransform: 'uppercase',
  },
}));

const FooterText = styled(Typography)(({ theme }) => ({
  color: 'white',
  fontWeight: 700,
  fontSize: '.75rem',
}));

const StyledImage = styled(Image)(({ theme }) => ({
  width: 30,
  height: 30,
  borderRadius: 9999,
}));

const CustomScrollbar = styled(Stack)({
  overflowY: 'hidden',
  scrollbarWidth: 'thin',
  scrollbarColor: 'grey #2b2b2b',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#2b2b2b',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#f48fb1',
    borderRadius: '10px',
    border: '2px solid #2b2b2b',
  },
});

const SocialIcons = styled(Image)(({ theme }) => ({
  width: 30,
  height: 30,
  cursor: 'pointer',
}));

const Footer = () => {
  const navigate = useNavigate();

  const { translate } = useLocales();

  const { siteSettings } = useSelector((x) => x.lookup);

  const [pages, setPages] = useState([]);

  const [activeCompanies, setActiveCompanies] = useState([]);

  const {
    config_footer_copyright,
    config_name,
    config_youtube,
    config_whatsapp,
    config_wechat,
    config_viber,
    config_twitter,
    config_telegram,
    config_messenger,
    config_line,
    config_instagram,
    config_facebook,
  } = siteSettings || {};

  useEffect(() => {
    /**
     * Get custom pages like TnC etc.
     */
    getPages();

    getActiveCompanies();
  }, []);

  const copyrightText = useMemo(() => {
    if (config_footer_copyright) {
      const newText = config_footer_copyright.replace('[year]', getYear(new Date())).replace('[name]', config_name);

      console.log(newText);

      return newText;
    }

    return '';
  }, [siteSettings?.config_footer_copyright, siteSettings?.config_name]);

  // const openSocial = (link) => {
  //   window.open(
  //     link,
  //     '_blank'
  //     // 'noopener noreferrer'
  //   );
  // };

  const getPages = () => {
    conObj
      .post(`/pages.json`)
      .then((res) => {
        if (res.status === 200) {
          if (res.data?.data?.data?.length > 0) {
            setPages(res.data?.data?.data?.map((item) => ({ pageKey: item?.page_key, title: item?.title })));
          }
        }
      })
      .catch((err) => {
        console.log(`Error: ${err?.message}`);
      });
  };

  const getActiveCompanies = () => {
    conObj.post('/games/active_companies.json').then((res) => {
      if (res.status === 200) {
        setActiveCompanies(res.data?.data?.data);
      }
    });
  };

  const onOpenSocial = (link) => {
    window.open(link, '_blank');
  };

  const displayGameProvidersComponent = (items) => {
    const midIndex = Math.ceil(items.length / 2);
    const firstHalf = items.slice(0, midIndex);
    const secondHalf = items.slice(midIndex);

    return (
      <CustomScrollbar
        direction={'column'}
        spacing={1}
        sx={{
          overflowX: 'scroll',
          mt: 2,
          ml: 0,
          width: '100%',
          padding: '0 20px 20px 10px',
        }}
      >
        <Stack direction={'row'} spacing={1} sx={{ height: '42px' }}>
          {firstHalf.map((item, index) => (
            <Image key={index} src={item?.logo_url} alt="Providers" sx={{ minWidth: '96px', height: '42px' }} />
          ))}
        </Stack>

        <Stack direction={'row'} spacing={1} sx={{ height: '42px' }}>
          {secondHalf.map((item, index) => (
            <Image key={index} src={item?.logo_url} alt="Providers" sx={{ minWidth: '96px', height: '42px' }} />
          ))}
        </Stack>
      </CustomScrollbar>
    );
  };

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* <Container maxWidth="lg" sx={{ backgroundColor: palette.footer.bgColor, p: '2rem 0 !important' }}>
        <Stack direction="row" alignItems={'center'} spacing={2} sx={{ ml: 2 }}>
          <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '.85rem' }}>
            {translate('our_game_provides')}
          </Typography>
        </Stack>
        {activeCompanies && displayGameProvidersComponent(activeCompanies?.filter((f) => f?.exists === true))}
      </Container> */}

      <Container maxWidth="lg" sx={{ p: 2, background: palette.footer.bgColor3 }}>
        <Stack spacing={1}>
          <FooterText sx={{ textAlign: 'center' }}>{translate('payment_method')}</FooterText>
          <Box style={{ marginTop: '1rem' }}>
            <Grid container spacing={'0.625rem'}>
              {_paymentMethod.map((src, idx) => (
                <Grid item key={idx}>
                  <StyledImage src={src} />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Stack>

        <Divider sx={{ mt: '1.75rem', mb: '.25rem', borderColor: 'rgb(59, 59, 59)' }} />

        <Grid container sx={{ mt: '.5rem' }}>
          <Grid item md={6} sm={6} xs={6}>
            <FooterText>{translate('contact')}</FooterText>
            <Stack direction="row" spacing={'0.5rem'} sx={{ mt: '.625rem' }}>
              {!!config_telegram && (
                <Box onClick={() => onOpenSocial(config_telegram)}>
                  <SocialIcons src={require('../../assets/social/telegram.png')} />
                </Box>
              )}
              {!!config_line && (
                <Box onClick={() => onOpenSocial(config_line)}>
                  <SocialIcons src={require('../../assets/social/line.png')} />
                </Box>
              )}
            </Stack>
          </Grid>
          <Grid item md={6} sm={6} xs={6}>
            <FooterText>{translate('responsible_for_betting')}</FooterText>
            <Image
              src={require('../../assets/footer/responsible-gaming.png')}
              sx={{
                mt: '.625rem',
                height: '30px',
                img: {
                  objectFit: 'contain',
                  width: 'auto',
                },
              }}
            />
          </Grid>
          {/* <Grid item md={4} sm={4} xs={4}>
            <FooterText>{translate('follow_us')}</FooterText>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              {!!config_youtube && (
                <Box onClick={() => onOpenSocial(config_youtube)}>
                  <SocialIcons src={require('../../assets/social/yt.png')} />
                </Box>
              )}

              {!!config_facebook && (
                <Box onClick={() => onOpenSocial(config_facebook)}>
                  <SocialIcons src={require('../../assets/social/fb.png')} />
                </Box>
              )}

              {!!config_instagram && (
                <Box onClick={() => onOpenSocial(config_instagram)}>
                  <SocialIcons src={require('../../assets/social/instagram.png')} />
                </Box>
              )}

              {!!config_whatsapp && (
                <Box onClick={() => onOpenSocial(config_whatsapp)}>
                  <SocialIcons src={require('../../assets/social/wa.png')} />
                </Box>
              )}

              {!!config_wechat && (
                <Box onClick={() => onOpenSocial(config_wechat)}>
                  <SocialIcons src={require('../../assets/social/wechat.png')} />
                </Box>
              )}

              {!!config_viber && (
                <Box onClick={() => onOpenSocial(config_viber)}>
                  <SocialIcons src={require('../../assets/social/viber.png')} />
                </Box>
              )}

              {!!config_twitter && (
                <Box onClick={() => onOpenSocial(config_twitter)}>
                  <SocialIcons src={require('../../assets/social/twitter.png')} />
                </Box>
              )}

              {!!config_telegram && (
                <Box onClick={() => onOpenSocial(config_telegram)}>
                  <SocialIcons src={require('../../assets/social/telegram.png')} />
                </Box>
              )}

              {!!config_messenger && (
                <Box onClick={() => onOpenSocial(config_messenger)}>
                  <SocialIcons src={require('../../assets/social/messenger.png')} />
                </Box>
              )}

              {!!config_line && (
                <Box onClick={() => onOpenSocial(config_line)}>
                  <SocialIcons src={require('../../assets/social/line.png')} />
                </Box>
              )}
            </Box>
          </Grid> */}
        </Grid>

        <Divider sx={{ mt: '1.75rem', mb: '.25rem', borderColor: 'rgb(59, 59, 59)' }} />

        <Grid container sx={{ mt: '.5rem' }}>
          <Grid item md={7} sm={7} xs={7}>
            <FooterText>{translate('licenses_and_certificates')}</FooterText>
            <Grid container spacing={'0.5rem'} sx={{ mt: '0.625rem'}}>
              {license_certs.map((item, index) => (
                <Grid item>
                  <Image
                    key={index}
                    src={item}
                    sx={{
                      // mt: '.625rem',
                      height: '30px',
                      img: {
                        objectFit: 'contain',
                        width: 'auto',
                      },
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Grid item md={5} sm={5} xs={5}>
            <FooterText>{translate('licenses_and_certificates')}</FooterText>
            <Image
              src={require('../../assets/footer/lnc6.png')}
              sx={{
                mt: '.625rem',
                height: '24px',
                img: {
                  objectFit: 'contain',
                  width: 'auto',
                },
              }}
            />
            <Image
              src={require('../../assets/footer/lnc7.png')}
              sx={{
                mt: '.625rem',
                height: '24px',
                img: {
                  objectFit: 'contain',
                  width: 'auto',
                },
              }}
            />
          </Grid>
        </Grid>

        {/* <Box sx={{ mt: 5 }}>
          <FooterText>{translate('app_download')}</FooterText>
          <Box
            onClick={() => {
              navigate('/download');
              window.location.reload();
            }}
            style={{ marginTop: 10 }}
          >
            <Image
              src={require('../../assets/download/android_download.png')}
              sx={{ height: '40px', width: '124px' }}
            />
          </Box>
        </Box> */}

        <Stack direction={'row'} spacing={1} sx={{ width: '100%', mt: '2rem' }}>
          {pages?.length > 0 &&
            pages.map((item, index) => (
              <Typography
                onClick={() => navigate(`/page/${item?.pageKey}`)}
                variant="caption"
                sx={{
                  cursor: 'pointer',
                  fontSize: '10px',
                  color: '#fff',
                  display: 'block',
                  fontWeight: 600,
                  width: 'auto',
                  textDecoration: 'underline',
                }}
              >
                {item?.title}
              </Typography>
            ))}
        </Stack>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mt: '2rem',
            textAlign: 'center',
          }}
        >
          <StyledTypography sx={{ fontSize: '.5rem' }}>{copyrightText}</StyledTypography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;

const _paymentMethod = [
  require('../../assets/footer/b1.png'),
  require('../../assets/footer/b2.png'),
  require('../../assets/footer/b3.png'),
  require('../../assets/footer/b4.png'),
  require('../../assets/footer/b5.png'),
  require('../../assets/footer/b6.png'),
  require('../../assets/footer/b7.png'),
  require('../../assets/footer/b8.png'),
  require('../../assets/footer/b9.png'),
  require('../../assets/footer/b10.png'),
  require('../../assets/footer/b11.png'),
  require('../../assets/footer/b12.png'),
  require('../../assets/footer/b13.png'),
  require('../../assets/footer/b14.png'),
  require('../../assets/footer/b15.png'),
];

const license_certs = [
  require('../../assets/footer/lnc1.png'),
  require('../../assets/footer/lnc2.png'),
  require('../../assets/footer/lnc3.png'),
  require('../../assets/footer/lnc4.png'),
  require('../../assets/footer/lnc5.png'),
];
